import styled from "@emotion/styled";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import { forwardRef, useEffect, useRef, useState } from "react";
import "./MapInput.css";

import { Loader } from "@googlemaps/js-api-loader";
import { CloseOutlined } from "@mui/icons-material";
const loader = new Loader({
  apiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
  libraries: ["places"],
});

//custom MUI textField
const CssTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#171717",
  },
  "& label.MuiInputLabel-formControl": {
    color: "#171717",
  },
  "& svg.MuiSvgIcon-root": {
    color: "#171717",
  },
  "& .MuiSelect-select": {
    color: "#171717",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#171717",
  },
  "& .MuiOutlinedInput-input": {
    color: "#171717",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#171717",
    },
    "&:hover fieldset": {
      borderColor: "#171717",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#171717",
    },
  },
  "& input:focus": {
    boxShadow: "none",
  },
});

const MapInput = forwardRef(({ field, onChange }, ref) => {
  const mapContainerRef = useRef();
  const inputRef = useRef();
  const [defaultLocation, setDefaultLocation] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: "", lng: "" });

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDefaultLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setMarkerPos({
          center: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
          zoom: 14,
        });
        setCoordinates({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        console.error("Geolocation error:", error);
        // fallback to a default (e.g., New Delhi)
        setDefaultLocation({
          lat: 10.014159652318847,
          lng: 76.36345920666948,
        });
        setMarkerPos({
          center: {
            lat: 10.014159652318847,
            lng: 76.36345920666948,
          },
          zoom: 14,
        });
        setCoordinates({
          lat: 10.014159652318847,
          lng: 76.36345920666948,
        });
      }
    );
  }, []);

  const [markerPos, setMarkerPos] = useState({
    center: defaultLocation,
    zoom: 14,
  });
  const [map, setMap] = useState();
  const [marker, setMarker] = useState();
  const [autocomplete, setAutocomplete] = useState();

  useEffect(() => {
    onChange(coordinates);
  }, [coordinates])

  // initialize google map
  useEffect(() => {
    if (mapContainerRef.current && inputRef.current) {
      async function initMap() {
        // loader.load() is deprecated, hence used 'imporLibrary()'
        // read through the doc for '@googlemaps/js-api-loader'
        try {
          const { Map } = await loader.importLibrary("maps");
          const { AdvancedMarkerElement } = await loader.importLibrary(
            "marker"
          );
          const { Autocomplete } = await loader.importLibrary("places");

          const options = {
            componentRestrictions: { country: "in" }, // search within India
            fields: ["geometry"], // return only the co-ordinates of the selected place
            strictBounds: false,
            types: ["establishment"],
          };
          const autocomplete = new Autocomplete(inputRef.current, options);

          const map = new Map(mapContainerRef.current, {
            center: markerPos.center,
            zoom: markerPos.zoom,
            disableDefaultUI: true,
            mapId: "VERIFIER_MAP",
          });

          const marker = new AdvancedMarkerElement({
            gmpDraggable: true,
            map: map,
            position: markerPos.center,
          });

          ref.current = markerPos.center;
          setMap(map);
          setMarker(marker);
          setAutocomplete(autocomplete);
        } catch (err) {
          console.log(err);
          // setting marker to Easter Islands if any error occurs
          setMarkerPos({
            center: { lat: -27.108758399543348, lng: -109.3557854189552 },
            zoom: 9,
          });
        }
      }

      initMap();
    }
  }, [mapContainerRef.current, markerPos, inputRef.current]);

  // listen to item click from autocomplete dropdown list
  useEffect(() => {
    autocomplete &&
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const pos = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        ref.current = pos;
        setMarkerPos({ center: pos, zoom: 16 });
        setCoordinates({
          lat: pos.lat.toFixed(6),
          lng: pos.lng.toFixed(6),
        });
      });
  }, [autocomplete]);

  // listen to drag end of the marker
  useEffect(() => {
    marker &&
      marker.addListener("dragend", (e) => {
        const { lat, lng } = e.latLng;
        const pos = { lat: lat(), lng: lng() };
        ref.current = pos;
        setCoordinates({
          lat: pos.lat.toFixed(6),
          lng: pos.lng.toFixed(6),
        });

        setMarkerPos({ center: pos, zoom: 16 });
      });
  }, [marker]);

  // set marker position to the current location
  useEffect(() => {
    if (map) {
      // to display error message
      const infoWindow = new window.google.maps.InfoWindow();

      // creating and positioning the "Current Location" button on the map
      const locationButton = document.createElement("button");
      locationButton.textContent = "Current Location";
      locationButton.classList.add("map-control-button");
      map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(
        locationButton
      );

      // set marker to current location when button is clicked
      locationButton.addEventListener("click", (e) => {
        e.preventDefault()
        // Try HTML5 geolocation.
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              ref.current = pos;
              setMarkerPos({ center: pos, zoom: 16 });
              setCoordinates({
                lat: position.coords.latitude.toFixed(6),
                lng: position.coords.longitude.toFixed(6),
              });
            },
            () => {
              window.handleLocationError(true, infoWindow, map.getCenter());
            }
          );
        } else {
          // Browser doesn't support Geolocation
          window.handleLocationError(false, infoWindow, map.getCenter());
        }
      });
    }
  }, [map]);

  // clear value in the input field
  const handleInputClear = () => {
    inputRef.current.value = "";
  };

  return (
    <Box>
      <Box display="flex" gap={1}>
        <CssTextField
          key={field.DISPLAY_NAME}
          required={field.REQUIRED}
          focused
          id={`outlined-${field.DISPLAY_NAME}`}
          label={"Latitude"}
          name={field.FIELD}
          value={coordinates.lat}
          sx={{ flex: 1 }}
        />
        <CssTextField
          key={field.DISPLAY_NAME}
          required={field.REQUIRED}
          focused
          id={`outlined-${field.DISPLAY_NAME}`}
          label={"Longitude"}
          name={field.FIELD}
          value={coordinates.lng}
          sx={{ flex: 1 }}
        />
      </Box>
      <TextField
        size="small"
        inputRef={inputRef}
        fullWidth
        placeholder="Search"
        sx={{ my: 1 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton disableRipple edge="end" onClick={handleInputClear}>
                <CloseOutlined />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Box borderRadius={1} ref={mapContainerRef} height={400} />
    </Box>
  );
});

export default MapInput;
